import { createClient } from '@supabase/supabase-js';

// Validate environment variables
if (!process.env.NEXT_PUBLIC_SUPABASE_URL) {
  throw new Error('Missing NEXT_PUBLIC_SUPABASE_URL');
}

if (!process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY) {
  throw new Error('Missing NEXT_PUBLIC_SUPABASE_ANON_KEY');
}

// Create client with anon key for client-side operations
const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
);

export interface MediaItem {
  id: string;
  url: string;
  title: string;
  path: string;
  created_at: string;
}

export async function uploadMediaFile(file: File, userId: string): Promise<MediaItem | null> {
  try {
    // Validate file
    if (!file || !userId) {
      throw new Error('Missing file or userId');
    }

    // Validate file size (50MB limit)
    if (file.size > 52428800) {
      throw new Error('File too large (max 50MB)');
    }

    // Create safe filename
    const timestamp = new Date().getTime();
    const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
    const safeName = `${timestamp}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${userId}/${safeName}`;

    console.log('Starting upload:', { filePath, type: file.type });

    // Direct upload attempt without bucket check
    const { data, error: uploadError } = await supabase.storage
      .from('user-library')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw uploadError;
    }

    if (!data?.path) {
      throw new Error('Upload succeeded but no path returned');
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('user-library')
      .getPublicUrl(data.path);

    console.log('Got public URL:', urlData);

    return {
      id: data.path,
      url: urlData.publicUrl,
      title: file.name,
      path: data.path,
      created_at: new Date().toISOString()
    };

  } catch (error) {
    console.error('Upload failed:', error);
    return null;
  }
}

export async function getUserLibrary(userId: string): Promise<MediaItem[]> {
  if (!userId) return [];

  try {
    const { data: files, error } = await supabase.storage
      .from('user-library')
      .list(userId);

    if (error) {
      console.error('List error:', error);
      return [];
    }

    return files.map(file => ({
      id: file.id || file.name,
      url: supabase.storage
        .from('user-library')
        .getPublicUrl(`${userId}/${file.name}`).data.publicUrl,
      title: file.name,
      path: `${userId}/${file.name}`,
      created_at: file.created_at || new Date().toISOString()
    }));

  } catch (error) {
    console.error('Get library error:', error);
    return [];
  }
}